import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Truck, MessageSquare, CheckCircle2 } from "lucide-react";
import api from "../../lib/api";

export default function AdminDashboard() {
  const [stats, setStats] = useState({ vehicles: 0, bookings: 0, pending: 0, inquiries: 0 });
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    (async () => {
      const [v, b, i] = await Promise.all([
        api.get("/admin/vehicles"),
        api.get("/admin/bookings"),
        api.get("/admin/inquiries"),
      ]);
      setStats({
        vehicles: v.data.length,
        bookings: b.data.length,
        pending: b.data.filter((x) => ["pending", "pending_payment", "confirmed_uninvoiced", "confirmed_payment_pending"].includes(x.status)).length,
        inquiries: i.data.length,
      });
      setRecent(b.data.slice(0, 5));
    })().catch(() => {});
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-heading text-2xl font-semibold tracking-tight">Dashboard</h1>
      <p className="text-sm text-slate-500">Quick overview of your IceFleet operations.</p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={<Truck size={16} />} label="Vehicles" value={stats.vehicles} href="/admin/vehicles" />
        <Stat icon={<CalendarDays size={16} />} label="Total bookings" value={stats.bookings} href="/admin/bookings" />
        <Stat icon={<CheckCircle2 size={16} />} label="Pending" value={stats.pending} href="/admin/bookings" highlight />
        <Stat icon={<MessageSquare size={16} />} label="Inquiries" value={stats.inquiries} href="/admin/inquiries" />
      </div>

      <div className="mt-10 rounded-xl border border-slate-200 bg-white">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Recent bookings</h2>
          <Link to="/admin/bookings" className="text-sm text-slate-600 hover:text-slate-900">View all →</Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No bookings yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {recent.map((b) => (
              <li key={b.id} className="px-5 py-3 flex items-center justify-between text-sm">
                <div>
                  <p className="font-medium text-slate-900">{b.customer_name} · {b.service_type}</p>
                  <p className="text-xs text-slate-500">{new Date(b.start_at).toLocaleDateString()} → {new Date(b.end_at).toLocaleDateString()}</p>
                </div>
                <StatusPill status={b.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({ icon, label, value, href, highlight }) {
  return (
    <Link
      to={href}
      data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
      className={`block rounded-xl border p-5 transition-colors ${highlight ? "border-sky-300 bg-sky-50 hover:bg-sky-100" : "border-slate-200 bg-white hover:border-slate-300"}`}
    >
      <div className="flex items-center gap-2 text-slate-600">
        {icon}<span className="text-xs font-bold uppercase tracking-widest">{label}</span>
      </div>
      <p className="mt-3 font-heading text-3xl font-bold tracking-tight">{value}</p>
    </Link>
  );
}

export function StatusPill({ status }) {
  const map = {
    pending_payment: "bg-amber-100 text-amber-900 ring-amber-200",
    confirmed_uninvoiced: "bg-sky-100 text-sky-900 ring-sky-200",
    confirmed_payment_pending: "bg-orange-100 text-orange-900 ring-orange-200",
    pending: "bg-amber-100 text-amber-900 ring-amber-200",
    confirmed: "bg-emerald-100 text-emerald-900 ring-emerald-200",
    cancelled: "bg-slate-200 text-slate-700 ring-slate-300",
    completed: "bg-slate-900 text-white ring-slate-900",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${map[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
}
